import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${window.location.origin}/api`;

// تحويل VAPID key من base64 إلى Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const usePushNotifications = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(false);

  // التحقق من دعم الإشعارات
  useEffect(() => {
    const checkSupport = () => {
      const supported = 'serviceWorker' in navigator && 
                       'PushManager' in window && 
                       'Notification' in window;
      setIsSupported(supported);
      
      if ('Notification' in window) {
        setPermission(Notification.permission);
      }
    };

    checkSupport();
  }, []);

  // التحقق من الاشتراك الحالي
  useEffect(() => {
    const checkSubscription = async () => {
      if (!isSupported) return;

      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      } catch (error) {
        console.error('Error checking subscription:', error);
      }
    };

    checkSubscription();
  }, [isSupported]);

  // طلب إذن الإشعارات
  const requestPermission = useCallback(async () => {
    if (!isSupported) return false;

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result === 'granted';
    } catch (error) {
      console.error('Error requesting permission:', error);
      return false;
    }
  }, [isSupported]);

  // الاشتراك في الإشعارات
  const subscribe = useCallback(async () => {
    if (!isSupported) return false;
    setLoading(true);

    try {
      // طلب الإذن أولاً
      const granted = await requestPermission();
      if (!granted) {
        setLoading(false);
        return false;
      }

      const registration = await navigator.serviceWorker.ready;
      
      // الحصول على VAPID public key من الخادم
      const { data: vapidData } = await axios.get(`${API}/notifications/vapid-key`);
      const vapidPublicKey = vapidData.public_key;

      // إنشاء اشتراك جديد
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // إرسال الاشتراك للخادم
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/notifications/subscribe`,
        { subscription: subscription.toJSON() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setIsSubscribed(true);
      setLoading(false);
      return true;
    } catch (error) {
      console.error('Error subscribing:', error);
      setLoading(false);
      return false;
    }
  }, [isSupported, requestPermission]);

  // إلغاء الاشتراك
  const unsubscribe = useCallback(async () => {
    if (!isSupported) return false;
    setLoading(true);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        // إلغاء الاشتراك محلياً
        await subscription.unsubscribe();
        
        // إلغاء الاشتراك من الخادم
        const token = localStorage.getItem('token');
        await axios.post(
          `${API}/notifications/unsubscribe`,
          { endpoint: subscription.endpoint },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      setIsSubscribed(false);
      setLoading(false);
      return true;
    } catch (error) {
      console.error('Error unsubscribing:', error);
      setLoading(false);
      return false;
    }
  }, [isSupported]);

  // إرسال إشعار محلي (للاختبار)
  const sendLocalNotification = useCallback((title, options = {}) => {
    if (!isSupported || permission !== 'granted') return;

    const defaultOptions = {
      icon: '/icons/icon-192x192.png',
      badge: '/icons/icon-96x96.png',
      vibrate: [200, 100, 200],
      tag: 'hesabaty-local',
      ...options
    };

    new Notification(title, defaultOptions);
  }, [isSupported, permission]);

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    requestPermission,
    subscribe,
    unsubscribe,
    sendLocalNotification
  };
};
